import { match } from 'ts-pattern'
import type { ServerAsset } from '@sequencehq/api/utils/commonModels'

export const MAX_FILE_COUNT = 10

export type ATTACHMENT_ERROR = 'MAX_FILE_COUNT_EXCEEDED'

type UserAddedAttachment = {
  clientId: string
  file: File
}

export type Asset = ServerAsset

type Attachment = UserAddedAttachment | Asset

export type AttachmentReducerState = {
  error: ATTACHMENT_ERROR | null
  attachments: Attachment[] | null
  attachmentToPreview: Asset | null
  isUploading: boolean
}

type UserPickedFilesAction = {
  type: 'USER_PICKED_FILES'
  files: File[]
}

type UploadSuccessAction = {
  type: 'UPLOAD_SUCCESS'
  clientId: string
  asset: Asset
}

type DeleteAttachmentAction = {
  type: 'DELETE_ATTACHMENT'
  attachment: Attachment
}

type PreviewAttachmentAction = {
  type: 'PREVIEW_ATTACHMENT'
  attachment: Asset | null
}

type SetAttachmentsAction = {
  type: 'SET_ATTACHMENTS'
  attachments: Attachment[] | null
}

export type AttachmentReducerAction =
  | UserPickedFilesAction
  | UploadSuccessAction
  | DeleteAttachmentAction
  | PreviewAttachmentAction
  | SetAttachmentsAction

export const attachmentReducer = (
  state: AttachmentReducerState,
  action: AttachmentReducerAction
): AttachmentReducerState => {
  return match<AttachmentReducerAction, AttachmentReducerState>(action)
    .with({ type: 'USER_PICKED_FILES' }, ({ files }) => {
      const attachments = [
        ...(state.attachments ?? []),
        ...files.map(file => ({
          file,
          clientId: crypto.randomUUID()
        }))
      ]

      if (attachments.length > MAX_FILE_COUNT) {
        return {
          ...state,
          error: 'MAX_FILE_COUNT_EXCEEDED'
        }
      }

      return {
        ...state,
        error: null,
        attachments,
        isUploading: true
      }
    })
    .with({ type: 'UPLOAD_SUCCESS' }, ({ clientId, asset }) => {
      const updatedAttachments =
        state.attachments?.map(attachment =>
          isUserAddedAttachment(attachment) && attachment.clientId === clientId
            ? asset
            : attachment
        ) ?? null

      const isUploading: boolean =
        (updatedAttachments ?? []).filter(attachment =>
          isUserAddedAttachment(attachment)
        ).length > 0

      return {
        ...state,
        attachments: updatedAttachments,
        isUploading
      }
    })
    .with({ type: 'DELETE_ATTACHMENT' }, ({ attachment }) => {
      return {
        ...state,
        attachmentToPreview: null,
        error: state.error === 'MAX_FILE_COUNT_EXCEEDED' ? null : state.error,
        attachments:
          state.attachments?.filter(
            currentAttachment =>
              !isSameAttachment(currentAttachment, attachment)
          ) ?? null
      }
    })
    .with({ type: 'PREVIEW_ATTACHMENT' }, ({ attachment }) => {
      return {
        ...state,
        attachmentToPreview: attachment
      }
    })
    .with({ type: 'SET_ATTACHMENTS' }, ({ attachments }) => {
      const incoming = attachments ?? []
      const incomingIds = new Set(
        incoming.filter(isServerAsset).map(asset => asset.id)
      )
      const preserved = (state.attachments ?? []).filter(attachment => {
        if (isUserAddedAttachment(attachment)) {
          return true
        }
        // Keep freshly-uploaded server assets during an in-flight multi-upload;
        // once settled the incoming payload is authoritative (rollback/deletion).
        return state.isUploading && !incomingIds.has(attachment.id)
      })

      return {
        ...state,
        attachments: [...incoming, ...preserved]
      }
    })
    .otherwise(() => state)
}

export const isUserAddedAttachment = (
  attachment: Attachment
): attachment is UserAddedAttachment => 'clientId' in attachment

export const isServerAsset = (attachment: Attachment): attachment is Asset =>
  'id' in attachment

const isSameAttachment = (a: Attachment, b: Attachment) => {
  if (
    isUserAddedAttachment(a) &&
    isUserAddedAttachment(b) &&
    a.clientId === b.clientId
  ) {
    return true
  }

  if (isServerAsset(a) && isServerAsset(b) && a.id === b.id) {
    return true
  }

  return false
}

export function noAttachments(state: AttachmentReducerState) {
  return !state.attachments || state.attachments.length === 0
}
